import {
  FileContent,
  useFilePicker,
  UseFilePickerConfig,
  Validator,
} from 'use-file-picker';
import { FeConstants } from 'constants/FeConstants';
import { FileError } from 'use-file-picker/dist/interfaces';
import { useEffect, useMemo, useState } from 'react';
import { parseFileError } from 'utils/uiHelpers';

const totalSizeValidatorOfNewFiles = (sizeSoFar: number): Validator => ({
  async validateBeforeParsing(
    config: UseFilePickerConfig,
    plainFiles: File[],
  ): Promise<void> {
    const newFilesTotalSizeInBytes = plainFiles.reduce(
      (acc, file) => acc + file.size,
      0,
    );
    const maxTotalSizeInBytes = FeConstants.uploadFileLimitInMb * 1_000_000;

    if (newFilesTotalSizeInBytes + sizeSoFar > maxTotalSizeInBytes) {
      throw new Error(FeConstants.maxLimitReachedMessage);
    }
  },
  async validateAfterParsing(config, file, reader): Promise<void> {
    // No additional validation needed after parsing
  },
});

export function useAttachmentsFilePicker(allAttachments: { size: number }[]): [
  () => void,
  {
    filesContent: FileContent[];
    errors: FileError[];
    errorMessage: string | null;
  },
] {
  const sizeSoFar = allAttachments.reduce((acc, curr) => acc + curr.size, 0);

  const [openFileSelector, { filesContent, errors, plainFiles }] =
    useFilePicker({
      readAs: 'DataURL',
      multiple: true,
      limitFilesConfig: { max: FeConstants.maxFilesPerTransaction },
      accept: [
        '.json',
        '.pdf',
        '.jpg',
        '.png',
        '.jpeg',
        '.webp',
        '.gif',
        '.doc',
        '.docx',
        '.xls',
        '.xlsx',
        '.ppt',
        '.pptx',
        '.txt',
        '.csv',
      ],
      validators: [totalSizeValidatorOfNewFiles(sizeSoFar)], // Pass sizeSoFar here
      maxFileSize: FeConstants.uploadFileLimitInMb,
    });

  const [errorUploadFile, setErrorUploadFile] = useState<string | null>(null);

  const filesWithSize = useMemo(() => {
    return filesContent.map((file, index) => ({
      ...file,
      size: plainFiles[index]?.size || 0,
    }));
  }, [filesContent, plainFiles]);

  useEffect(() => {
    let t: NodeJS.Timeout;
    if (errors.length > 0) {
      const firstError = errors[0];
      const message = parseFileError(firstError);

      setErrorUploadFile(message);
      t = setTimeout(() => {
        setErrorUploadFile(null);
      }, 5000);
    }
    return () => {
      clearTimeout(t);
    };
  }, [errors]);

  return [
    openFileSelector,
    {
      filesContent: filesWithSize,
      errors,
      errorMessage: errorUploadFile,
    },
  ];
}
